/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { FirebaseListFactory } from 'src/app/common/fancy-firebase/services/factories';
import { Attachments } from '../../app-data/state/Attachments';
import { FileAttachApi } from '../../app-data/state/FileAttachApi';
import { FancyFragmenter } from '../../common/fancy-firebase/base/FancyFragmenter';
import { AttachmentList } from '../../common/fancy-firebase/lists/AttachmentList';
import { ProjectFileList } from '../../common/fancy-firebase/lists/ProjectFileList';
import { DefaultAngularLand } from '../../common/fancy-firebase/util/default-angular-land.service';
import { FileAttachCloneService } from '../../modules/file-attach/file-attach-clone.service';
import { ProjectFileAttachApi } from 'src/app/app-data/state/ProjectFileAttachApi';
import { FeatureFlagManager } from '../FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FirebaseCloudFunctionsService } from '../firebase-cloud-functions/cloud-functions.service';
import { HttpClient } from '@angular/common/http';

import firebase from 'firebase/compat/app';

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseFileAttachApiBuilder {
	constructor(
		private firebaseListFactory: FirebaseListFactory,
		private defaultAngularLand: DefaultAngularLand,
		private fbCloudFunctionsService: FirebaseCloudFunctionsService,
		private fileAttachCloneService: FileAttachCloneService,
		private featureFlagManager: FeatureFlagManager,
		private http: HttpClient
	) {}

	public getFileAttachApi(
		projectId: string,
		planId: string,
		ticketId: string,
		userId: string
	): FileAttachApi {
		const projectFilesPath = fancyFirebaseGetters.getProjectFilesPath(projectId);
		const ticketAttachmentsPath = fancyFirebaseGetters.getTicketAttachmentsPath(projectId, planId, ticketId);

		const projectFilesFragment = new FancyFragmenter(
			this.defaultAngularLand,
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectFilesPath, projectId)
				.child('ticketAttachments')
		);
		const projectFilesList = this.firebaseListFactory.create(ProjectFileList, projectFilesFragment.emitter$);
		projectFilesList.listRef = this.defaultAngularLand.angularFireDatabase.list(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectFilesPath, projectId)
				.child('ticketAttachments')
		);
		projectFilesFragment.start();

		const thumbnailFragmentJoiner = new FancyIdJoin(
			'thumbnailFileId',
			projectFilesList,
			projectFilesFragment
		);
		const previewFragmentJoiner = new FancyIdJoin('previewFileId', projectFilesList, projectFilesFragment);
		const attachmentList = this.firebaseListFactory.create(
			AttachmentList,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketAttachmentsPath, projectId),
			[thumbnailFragmentJoiner, previewFragmentJoiner]
		);
		const attachments = new Attachments(attachmentList);

		attachmentList.addDestroyer(
			() => projectFilesFragment.destroy(),
			() => projectFilesList.destroy(),
			() => thumbnailFragmentJoiner.destroy(),
			() => previewFragmentJoiner.destroy(),
			() => attachments.destroy()
		);

		return new FileAttachApi(
			this.defaultAngularLand.angularFireDatabase,
			this.fbCloudFunctionsService,
			this.fileAttachCloneService,
			attachments,
			projectId,
			planId,
			ticketId,
			userId,
			//only supporting the sync version because of all the other changes required
			this.featureFlagManager.getFlag(DynamicFlagTypes.ENABLE_GCS_TICKET_ATTACHMENTS_PATH_SEGMENT),
			this.http
		);
	}

	public getProjectFileAttachApi(projectId: string, userId: string): ProjectFileAttachApi {
		const projectFilesPath = fancyFirebaseGetters.getProjectFilesPath(projectId);
		const projectAttachmentsPath = fancyFirebaseGetters.getProjectAttachmentsPath(projectId);

		const projectFilesFragment = new FancyFragmenter(
			this.defaultAngularLand,
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectFilesPath, projectId)
				.child('projectAttachments')
		);
		const projectFilesList = this.firebaseListFactory.create(ProjectFileList, projectFilesFragment.emitter$);
		projectFilesList.listRef = this.defaultAngularLand.angularFireDatabase.list(
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(projectFilesPath, projectId)
				.child('projectAttachments')
		);
		projectFilesFragment.start();

		const fileFragmentJoiner = new FancyIdJoin('fileId', projectFilesList, projectFilesFragment);

		const attachmentList = this.firebaseListFactory.create(
			AttachmentList,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(projectAttachmentsPath, projectId),
			[fileFragmentJoiner]
		);
		const attachments = new Attachments(attachmentList);

		attachmentList.addDestroyer(
			() => attachments.destroy(),
			() => fileFragmentJoiner.destroy()
		);

		return new ProjectFileAttachApi(this.fbCloudFunctionsService, attachments);
	}
}
